import { useState, useRef, useEffect } from 'react';
import { ChevronDown } from 'lucide-react';
import countries from '../lib/countries';

export function PhoneInput({ value, onChange, selectedCountry, onCountryChange, required }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const dropdownRef = useRef(null);
  const searchRef = useRef(null);

  const country = countries.find((c) => c.code === selectedCountry) || countries.find((c) => c.code === 'US');

  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    if (open && searchRef.current) searchRef.current.focus();
  }, [open]);

  const filtered = search
    ? countries.filter(
        (c) =>
          c.name.toLowerCase().includes(search.toLowerCase()) ||
          c.dial.includes(search) ||
          c.code.toLowerCase().includes(search.toLowerCase())
      )
    : countries;

  const handleSelect = (c) => {
    onCountryChange(c.code);
    // Replace old dial code with new one
    const oldDial = country.dial;
    const newDial = c.dial;
    if (value.startsWith(oldDial)) {
      onChange(newDial + value.slice(oldDial.length));
    } else if (!value || value === oldDial) {
      onChange(newDial + ' ');
    }
    setOpen(false);
    setSearch('');
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <div className="flex h-9 w-full rounded-md border border-input bg-transparent shadow-sm">
        <button
          type="button"
          onClick={() => setOpen(!open)}
          className="flex items-center gap-1 px-2.5 border-r border-input hover:bg-accent/50 rounded-l-md transition-colors shrink-0"
          data-testid="phone-country-selector"
        >
          <span className="text-base leading-none">{country.flag}</span>
          <span className="text-xs text-muted-foreground font-medium">{country.dial}</span>
          <ChevronDown className="h-3 w-3 text-muted-foreground" />
        </button>
        <input
          type="tel"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={`${country.dial} `}
          required={required}
          className="flex-1 bg-transparent px-3 py-1 text-sm outline-none placeholder:text-muted-foreground"
          data-testid="guest-phone-input"
        />
      </div>

      {open && (
        <div className="absolute z-50 mt-1 w-72 bg-popover border border-border rounded-lg shadow-lg overflow-hidden" data-testid="country-dropdown">
          <div className="p-2 border-b border-border">
            <input
              ref={searchRef}
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search country..."
              className="w-full px-2.5 py-1.5 text-sm bg-transparent border border-input rounded-md outline-none placeholder:text-muted-foreground focus:ring-1 focus:ring-ring"
              data-testid="country-search-input"
            />
          </div>
          <div className="max-h-52 overflow-y-auto">
            {filtered.map((c) => (
              <button
                key={c.code}
                type="button"
                onClick={() => handleSelect(c)}
                className={`w-full flex items-center gap-2.5 px-3 py-2 text-sm hover:bg-accent transition-colors ${
                  c.code === selectedCountry ? 'bg-accent/60 font-medium' : ''
                }`}
                data-testid={`country-option-${c.code}`}
              >
                <span className="text-base leading-none">{c.flag}</span>
                <span className="flex-1 text-left truncate">{c.name}</span>
                <span className="text-xs text-muted-foreground">{c.dial}</span>
              </button>
            ))}
            {filtered.length === 0 && (
              <div className="px-3 py-4 text-sm text-muted-foreground text-center">No countries found</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
